import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { connectorsByIdentifier } from "../../../shared/connectors";
import { connectionTestWouldSkip } from "../../../shared/connectors/connectionTestWouldSkip";
import {
  isConfigured,
  isConfiguredForConnectionTest,
} from "../../../shared/connectors/isDraft";
import { sharedMutators } from "../../../shared/mutators";
import { useVisitorTestReadiness } from "../../hooks/useVisitorTestReadiness";
import type { Connector } from "../../lib/connector";
import { useMakeUrl, routes } from "../../lib/urls";
import {
  type ConnectorStatus,
  getConnectorStatus,
  getConnectorStatusTooltip,
} from "../../lib/connectorStatus";
import { useConnectorStatusTooltip } from "./useConnectorStatusTooltip";
import { ConnectorStatusBadge } from "./ConnectorStatusBadge";

const statusLabels: Record<ConnectorStatus, string> = {
  active: "Active",
  untested: "Untested",
  error: "Error",
  draft: "Draft",
  disabled: "Disabled",
};

type Props = {
  app: Connector;
  showDetails?: boolean;
};

export function ConnectorStatusPill({ app, showDetails }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const definition = connectorsByIdentifier[app.identifier];
  const { visitor, canManage, visitorReady } = useVisitorTestReadiness(app);
  const supportsConnectionTest =
    app.identifier !== "workflow" && (!visitor || visitorReady);
  const configured = isConfigured(app);
  const untestable = connectionTestWouldSkip(
    definition?.connectionTest,
    app.configuration
  );
  const showsResult = !visitor || app.lastTestUserId === z.userID;
  const isPending = showsResult && app.lastTestStatus === "pending";

  // Hold the last real status while a re-test is pending, so the pill doesn't
  // flicker back to a neutral state mid-test.
  const [renderedStatus, setRenderedStatus] = useState(app.lastTestStatus);
  if (!["pending", renderedStatus].includes(app.lastTestStatus)) {
    setRenderedStatus(app.lastTestStatus);
  }
  const displayStatus = getConnectorStatus({
    ...app,
    lastTestStatus: renderedStatus,
  });

  const errorMessage =
    renderedStatus === "failure" && app.lastTestMessage
      ? app.lastTestMessage
      : null;

  // Gate on the rendered status so a draft pill (which outranks the OAuth
  // error in getConnectorStatus) never carries an OAuth-error tooltip.
  const oauthError =
    displayStatus === "error" && app.oauthConnection?.status === "error"
      ? app.oauthConnection.lastError || "Reconnect this connector"
      : null;

  const connectionTestTooltip = useConnectorStatusTooltip({
    identifier: app.identifier,
    configured,
    isPending,
    status: showsResult ? renderedStatus : null,
    lastTestAt: app.lastTestAt,
    lastTestMessage: errorMessage,
    oauthError,
    untestable,
  });
  const tooltip =
    (visitor && canManage && displayStatus === "active"
      ? visitorReady
        ? connectionTestTooltip?.text
        : "Connect this connector with your own account first."
      : null) ??
    getConnectorStatusTooltip(app, displayStatus) ??
    (supportsConnectionTest ? connectionTestTooltip?.text : null) ??
    null;

  return (
    <ConnectorStatusBadge
      status={displayStatus}
      label={statusLabels[displayStatus]}
      detail={tooltip}
      httpStatusCode={
        displayStatus === "error"
          ? connectionTestTooltip?.httpStatusCode
          : undefined
      }
      running={isPending}
      untestable={untestable}
      onTest={
        supportsConnectionTest &&
        (visitor || isConfiguredForConnectionTest(app))
          ? () =>
              z.mutate(
                sharedMutators.testConnectedApp({ connectedAppId: app.id })
              )
          : undefined
      }
      detailsUrl={
        showDetails && displayStatus !== "draft"
          ? makeUrl(routes.modal.connectorTest, { id: app.id })
          : undefined
      }
    />
  );
}
